import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RestoreIcon from "@mui/icons-material/Restore";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { API_URL } from '../utils/apiConfig';

const SimpleReinstateEmployees = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [reinstatementDate, setReinstatementDate] = useState(dayjs());
  const [reinstatementReason, setReinstatementReason] = useState("");
  const [step, setStep] = useState(1); // 1: Select Employees, 2: Review & Confirm
  const [reinstatementResults, setReinstatementResults] = useState(null);

  // Get user role and store
  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.role === "admin";
  const managerStore = user?.store;
  const managerLocation = user?.location;

  // Fetch archived employees
  useEffect(() => {
    const fetchArchivedEmployees = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/employees?includeArchived=true`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
  
        if (!response.ok) {
          throw new Error("Failed to fetch employees");
        }
  
        const data = await response.json();
        
        console.log('All employees data:', data);
        console.log('Sample employee fields:', data[0]);
        
        // Filter only archived employees
        let archived = data.filter(emp => {
          const isArchived = emp.current_employement_status === 'Archived' ||
                           emp.currentEmploymentStatus === 'Archived';
          console.log(`Employee ${emp.firstName} ${emp.lastName}: status=${emp.current_employement_status}, isArchived=${isArchived}`);
          return isArchived;
        });
        
        // If manager, filter by their location
        if (!isAdmin && (managerStore || managerLocation)) {
          archived = archived.filter(emp => 
            emp.locationId == managerStore || 
            emp.location === managerLocation ||
            emp.store == managerStore
          );
        }
        
        setArchivedEmployees(archived);
        setFilteredEmployees(archived);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchArchivedEmployees();
  }, []);

  // Filter employees based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredEmployees(archivedEmployees);
    } else {
      const filtered = archivedEmployees.filter((emp) => {
        const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
        const email = emp.email?.toLowerCase() || "";
        const position = emp.position?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        
        return fullName.includes(search) || 
               email.includes(search) || 
               position.includes(search);
      });
      setFilteredEmployees(filtered);
    }
  }, [searchTerm, archivedEmployees]);

  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(employeeId)) {
        return prev.filter((id) => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map((emp) => emp.id));
    }
  };

  const handleReinstate = async () => {
    if (!reinstatementReason.trim()) {
      setError('Please provide a reason for reinstatement');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/employees/batch-reinstate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employeeIds: selectedEmployees,
          reinstatementDate: dayjs().format('YYYY-MM-DD'), // Always use today's date
          reason: reinstatementReason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reinstate employees');
      }

      const data = await response.json();
      console.log('Reinstatement response:', data);
      console.log('Results:', data.results);
      setReinstatementResults(data.results);
      setSuccess(true);
      
      // Remove reinstated employees from the list
      const remainingEmployees = archivedEmployees.filter(
        emp => !data.results.successful.some(s => s.employeeId === emp.id)
      );
      setArchivedEmployees(remainingEmployees);
      setFilteredEmployees(remainingEmployees);
      setSelectedEmployees([]);
      setReinstatementReason('');
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedEmployeeDetails = archivedEmployees.filter(emp => 
    selectedEmployees.includes(emp.id)
  );

  if (success && reinstatementResults) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <RestoreIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Employees Reinstated Successfully
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {reinstatementResults.successful.length} employee(s) have been reinstated
        </Typography>
        
        {reinstatementResults.failed.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
            {reinstatementResults.failed.length} employee(s) could not be reinstated
          </Alert>
        )}
        
        {reinstatementResults.successful.some(r => r.dataRestored === false) && (
          <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
            Some sensitive data could not be restored from encryption
          </Alert>
        )}
        
        <Button variant="contained" onClick={onClose} fullWidth>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ 
        height: isMobile ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            <RestoreIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reinstate Employees
          </Typography>
          
          {step === 1 && (
            <Typography variant="body2" color="text.secondary">
              Select archived employees to reinstate
            </Typography>
          )}
          
          {step === 2 && (
            <Typography variant="body2" color="text.secondary">
              Review and confirm reinstatement details
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden', px: 3 }}>
          {step === 1 && (
            <Stack spacing={2}>
              {/* No archived employees message */}
              {archivedEmployees.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="h6" color="text.secondary">
                    No archived employees found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    All employees are currently active
                  </Typography>
                </Box>
              )}

              {archivedEmployees.length > 0 && (
                <>
                  {/* Search */}
                  <TextField
                    fullWidth
                    placeholder="Search archived employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm("")}>
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Select All */}
                  {filteredEmployees.length > 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedEmployees.length === filteredEmployees.length}
                          indeterminate={
                            selectedEmployees.length > 0 &&
                            selectedEmployees.length < filteredEmployees.length
                          }
                          onChange={handleSelectAll}
                        />
                      }
                      label={`Select All (${filteredEmployees.length})`}
                    />
                  )}

                  {/* Employee List */}
                  <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    maxHeight: isMobile ? '40vh' : '50vh',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : filteredEmployees.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          {searchTerm
                            ? "No archived employees found matching search"
                            : "No archived employees available"}
                        </Typography>
                      </Box>
                    ) : (
                      <List sx={{ py: 0 }}>
                        {filteredEmployees.map((employee) => (
                          <ListItem
                            key={employee.id}
                            button
                            onClick={() => handleEmployeeToggle(employee.id)}
                            selected={selectedEmployees.includes(employee.id)}
                            divider
                            sx={{ py: 1 }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                checked={selectedEmployees.includes(employee.id)}
                                tabIndex={-1}
                                disableRipple
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${employee.firstName} ${employee.lastName}`}
                              secondary={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    {employee.position}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {employee.email}
                                  </Typography>
                                  {employee.fireDate && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Archived: {new Date(employee.fireDate).toLocaleDateString()}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={3}>
              {/* Info Alert */}
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Reinstatement Process:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>Reactivates employee login access</li>
                  <li>Restores encrypted data when possible</li>
                  <li>Sets employee status back to Active</li>
                  <li>Updates hire date to reinstatement date</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Note:</strong> Employees will need to be notified separately
                </Typography>
              </Alert>

              {/* Selected Employees */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    <Badge badgeContent={selectedEmployees.length} color="primary" sx={{ mr: 2 }}>
                      Selected Employees
                    </Badge>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedEmployeeDetails.map((emp) => (
                      <ListItem key={emp.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={`${emp.firstName} ${emp.lastName}`}
                          secondary={emp.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Reinstatement Details */}
              <TextField
                fullWidth
                label="Reinstatement Date"
                value={dayjs().format('MMMM D, YYYY')}
                disabled
                helperText="Employees will be reinstated with today's date"
              />

              <TextField
                fullWidth
                label="Reason for Reinstatement"
                value={reinstatementReason}
                onChange={(e) => setReinstatementReason(e.target.value)}
                multiline
                rows={3}
                required
                placeholder="e.g., Position reopened, Rehired for new role, Error in termination, etc."
              />
            </Stack>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ p: 3, pt: 2 }}>
          {step === 1 && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={onClose}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep(2)}
                disabled={selectedEmployees.length === 0}
                fullWidth
                startIcon={<RestoreIcon />}
              >
                Continue ({selectedEmployees.length})
              </Button>
            </Stack>
          )}

          {step === 2 && (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                fullWidth
              >
                Back
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleReinstate}
                disabled={loading || !reinstatementReason.trim()}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
              >
                {loading ? "Reinstating..." : "Reinstate Employees"}
              </Button>
            </Stack>
          )}
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SimpleReinstateEmployees;